import { type FC } from 'react'

import { Box, Button, Radio, SelectOption, Text } from '@factorialco/deprecated-design-system'
import {
  DraftSalaryRangeModalLegalEntitiesFragment,
  DraftSalaryRangeModalRoleLegalEntitiesFragment,
  SalaryRangesTableRowRoleFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import MoneyField from 'modules/core/components/MoneyField'
import SelectField from 'modules/core/components/SelectField'
import Selector from 'modules/core/components/Selector'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import useCreateSalaryRange from 'modules/job_catalog/hooks/useCreateSalaryRange'
import useUpdateSalaryRange from 'modules/job_catalog/hooks/useUpdateSalaryRange'
import { salaryToCents, salaryToString } from 'modules/job_catalog/lib/salaryUtils'

interface Props {
  roleId: string
  roleName: string
  draft?: SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges'][number]
  levels?: SalaryRangesTableRowRoleFragment['levels']
  roleLegalEntities?: DraftSalaryRangeModalRoleLegalEntitiesFragment['legalEntities']
  legalEntities?: DraftSalaryRangeModalLegalEntitiesFragment[]
  onClose: () => void
  onSuccess: () => void
}

interface FormValues {
  levelId: string
  minRange: string
  maxRange: string
  currency: string
  salaryFrequency: string
  legalEntitiesIds: string[]
  amountType: string
}

const DraftSalaryRangeModal: FC<Props> = ({
  roleId,
  roleName,
  draft: initialDraft,
  roleLegalEntities,
  legalEntities,
  onClose,
  onSuccess,
  levels,
}) => {
  const createSalaryRange = useCreateSalaryRange()
  const updateSalaryRange = useUpdateSalaryRange()
  const frequencies = ContractVersion.SalaryFrequencies()
  const isEditing = !!initialDraft
  const salaryRanges = levels?.reduce<
    SalaryRangesTableRowRoleFragment['levels'][number]['salaryRanges']
  >((acc, x) => {
    return [...acc, ...x.salaryRanges]
  }, [])

  const handleUpdateSalaryRange = async (
    id: string,
    { amountType, currency, legalEntitiesIds, maxRange, minRange, salaryFrequency }: FormValues
  ) => {
    const result = await updateSalaryRange(
      id,
      amountType,
      currency,
      legalEntitiesIds,
      salaryToCents(maxRange) ?? 0,
      salaryToCents(minRange) || 0,
      salaryFrequency
    )
    const handled = handleResult(result, (errors) => {
      form.setErrors({ ...errors })
    })

    return handled
  }

  const handleCreateSalaryRange = async ({
    amountType,
    currency,
    legalEntitiesIds,
    levelId,
    maxRange,
    minRange,
    salaryFrequency,
  }: FormValues) => {
    const result = await createSalaryRange(
      amountType,
      currency,
      legalEntitiesIds,
      levelId,
      salaryToCents(maxRange) ?? 0,
      salaryToCents(minRange) ?? 0,
      salaryFrequency
    )
    const handled = handleResult(result, (errors) => {
      form.setErrors({ ...errors })
    })

    return handled
  }

  const form = useForm<FormValues>({
    defaultValues: {
      levelId: initialDraft?.level?.id || '-1',
      salaryFrequency: initialDraft?.salaryFrequency || (Object.keys(frequencies)[0] as string),
      amountType: initialDraft?.amountType || 'fixed',
      currency: initialDraft?.currency || '',
      legalEntitiesIds: initialDraft?.legalEntities.map((x) => x.id) || [],
      maxRange: salaryToString(initialDraft?.maxRange ?? 0),
      minRange: salaryToString(initialDraft?.minRange ?? 0),
    },
    onSubmit: async (values) => {
      const handled = initialDraft
        ? await handleUpdateSalaryRange(initialDraft.id, values)
        : await handleCreateSalaryRange(values)

      if (handled.success) {
        onSuccess()
      }

      return handled
    },
    validate: (values) => {
      const errors: FormErrors<FormValues> = {}

      if (values.amountType === 'range' && String(values.maxRange) <= String(values.minRange)) {
        errors.maxRange = i18n.t('job_catalog.salary_ranges.range_error')
      }

      const isDuplicated = salaryRanges
        ?.filter((x) => x.id !== initialDraft?.id)
        ?.some(
          (x) =>
            x.level?.id === values.levelId &&
            x.legalEntities.some((y) => values.legalEntitiesIds.includes(y.id))
        )

      if (isDuplicated) {
        errors.legalEntitiesIds = i18n.t('job_catalog.salary_ranges.duplicated_error')
      }

      return errors
    },
  })

  const jobLevelSelectOptions = levels
    ?.filter((x) => x.name !== '__default__')
    ?.map((x) => ({ label: x.name, value: x.id, order: x.order }))
    .sort((a, b) => a.order - b.order)

  const typeSelectOptions: SelectOption[] = Object.keys(frequencies).map((key) => ({
    label: frequencies[key] ?? '',
    value: key,
  }))

  const legalEntitiesOptions = roleLegalEntities?.map((x) => {
    const { id, legalName, currency } = x
    const isDisabled = !!form.values.currency && form.values.currency !== currency

    return (
      <Selector.Option
        key={id}
        value={id}
        label={legalName || ''}
        searchValue={legalName || ''}
        isDisabled={isDisabled}
      >
        <Box width="full" overflowY="hidden">
          <Text.Normal
            size="200"
            weight="medium"
            ellipsis
            color={isDisabled ? 'grey600' : undefined}
          >
            {legalName}
          </Text.Normal>
        </Box>
        <Text.Normal size="200" weight="medium" color={isDisabled ? 'grey600' : undefined}>
          {currency}
        </Text.Normal>
      </Selector.Option>
    )
  })

  const handleChangeLegalEntities = (ids: string[]) => {
    const firstLegalEntity = legalEntities?.find((x) => x.id === ids[0])

    if (!firstLegalEntity) return

    const currency = firstLegalEntity.currency

    form.setValues({ ...form.values, legalEntitiesIds: ids, currency: currency as string })
  }

  const handleChangeJobLevel = (selected: string) => {
    form.setValues({ ...form.values, levelId: String(selected) })
  }

  const handleChangeJobLevelType = (selected: string) => {
    form.setValues({ ...form.values, salaryFrequency: selected })
  }

  const invalidNumberError = (number: string) => {
    const error = i18n.t('job_catalog.salary_ranges.invalid_number_error', {
      number,
    })
    return error
  }

  const handleChangeMinRange = (nextMinRange: string) => {
    if (!nextMinRange) {
      form.setValues({ ...form.values, minRange: nextMinRange })
    }

    const nextRange = salaryToCents(nextMinRange)
    if (!nextRange) {
      form.setFieldError('minRange', invalidNumberError(nextMinRange))
      return
    }
    form.setValues({ ...form.values, minRange: nextMinRange })
  }

  const handleChangeMaxRange = (nextMaxRange: string) => {
    if (!nextMaxRange) {
      form.setValues({ ...form.values, maxRange: nextMaxRange })
    }
    const nextRange = salaryToCents(nextMaxRange)
    if (!nextRange) {
      form.setFieldError('maxRange', invalidNumberError(nextMaxRange))
      return
    }

    form.setValues({ ...form.values, maxRange: nextMaxRange })
  }

  const handleChangeFixedAmount = (nextFixedAmount: string) => {
    if (!nextFixedAmount) {
      form.setValues({ ...form.values, minRange: '', maxRange: '' })
    }
    const nextRange = salaryToCents(nextFixedAmount)
    if (!nextRange) {
      form.setFieldError('maxRange', invalidNumberError(nextFixedAmount))
      return
    }
    form.setValues({ ...form.values, minRange: nextFixedAmount, maxRange: nextFixedAmount })
  }

  const handleChangeType = (nextAmountType: string) => {
    form.setValues({ ...form.values, amountType: nextAmountType })
  }

  const handleClose = () => {
    onClose()
  }

  const title = isEditing
    ? i18n.t('job_catalog.salary_ranges.edit_salary_range_modal.title')
    : i18n.t('job_catalog.salary_ranges.new_salary_range_modal.title')
  return (
    <Modal
      illustration="coins"
      title={title}
      description={i18n.t('job_catalog.salary_ranges.new_salary_range_modal.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={handleClose}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!form.isValid} submit>
            {i18n.t('common.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <SelectField
          label={i18n.t('job_catalog.salary_ranges.job_role')}
          readonly
          options={[{ label: roleName, value: roleId }]}
        />
        <SelectField
          label={i18n.t('job_catalog.level')}
          options={jobLevelSelectOptions || []}
          readonly={isEditing}
          none={i18n.t('common.choose')}
          value={form.values.levelId.toString()}
          onChange={handleChangeJobLevel}
          mandatory
        />
        <Selector.Popover
          label={i18n.t('common.legal_entities')}
          blankLabel={i18n.t('common.empty')}
          allSelectedLabel={i18n.t('common.all')}
          placeholder={i18n.t('common.select')}
          searchPlaceholder={i18n.t('common.search')}
          icon="office"
          value={form.values.legalEntitiesIds}
          onChange={(ids) => handleChangeLegalEntities(ids as string[])}
          isMultiSelect
          mandatory
        >
          {legalEntitiesOptions || []}
        </Selector.Popover>
        <SelectField
          label={i18n.t('job_catalog.salary_ranges.type')}
          options={typeSelectOptions}
          value={form.values.salaryFrequency}
          onChange={handleChangeJobLevelType}
        />
        <Radio.Wrap>
          <Radio
            name="amount_type"
            value={form.values.amountType}
            onChange={handleChangeType}
            description={i18n.t('job_catalog.salary_ranges.fixed_amount_description')}
            option={{
              label: i18n.t('job_catalog.salary_ranges.fixed_amount'),
              value: 'fixed',
            }}
          >
            <MoneyField
              label={i18n.t('job_catalog.salary_ranges.amount')}
              currency={form.values.currency || '-'}
              onChange={handleChangeFixedAmount}
              mandatory
              value={form.values.maxRange}
              error={form.errors.maxRange}
            />
          </Radio>
        </Radio.Wrap>
        <Radio.Wrap>
          <Radio
            name="amount_type"
            value={form.values.amountType}
            onChange={handleChangeType}
            description={i18n.t('job_catalog.salary_ranges.range_description')}
            option={{
              label: i18n.t('job_catalog.salary_ranges.range'),
              value: 'range',
            }}
          >
            <Box flexDirection="row" justifyContent="spaceBetween" gap="s24">
              <MoneyField
                label={i18n.t('job_catalog.salary_ranges.min')}
                currency={form.values.currency || '-'}
                mandatory
                value={form.values.minRange}
                onChange={handleChangeMinRange}
                error={form.errors.minRange}
              />
              <MoneyField
                label={i18n.t('job_catalog.salary_ranges.max')}
                currency={form.values.currency || '-'}
                mandatory
                value={form.values.maxRange}
                onChange={handleChangeMaxRange}
                error={form.errors.maxRange}
              />
            </Box>
          </Radio>
        </Radio.Wrap>
        {form.errors.legalEntitiesIds && (
          <Text.Normal size="200" color="radicalRed1000">
            {form.errors.legalEntitiesIds}
          </Text.Normal>
        )}
      </Box>
    </Modal>
  )
}

export default DraftSalaryRangeModal
